import type { OperationalProcess } from './operationalProcessTypes';
import { PROCESS_STATUS_LABELS, PROCESS_TYPE_LABELS, type OperationalProcessType } from './processTypes';

export type ProcessSummaryLine = {
  id: string;
  label: string;
  statusLabel: string;
  tone: 'neutral' | 'active' | 'waiting' | 'done';
  overdue?: boolean;
  responsibleDisplayName?: string | null;
  slaExpectedAt?: string | null;
  overdueDurationLabel?: string | null;
  escalationCandidateDisplayName?: string | null;
};

function statusTone(status: string): ProcessSummaryLine['tone'] {
  if (status === 'COMPLETED' || status === 'CANCELLED') return 'done';
  if (status === 'WAITING') return 'waiting';
  if (status === 'ACTIVE' || status === 'CREATED') return 'active';
  return 'neutral';
}

function formatSlaRemaining(expectedAt: string | null | undefined): string | null {
  if (!expectedAt) return null;
  const diff = new Date(expectedAt).getTime() - Date.now();
  if (diff <= 0) return null;
  const hours = Math.floor(diff / 3_600_000);
  if (hours < 48) return `${hours}ч`;
  return `${Math.floor(hours / 24)}д`;
}

export function buildOperationalProcessSummary(
  processes: OperationalProcess[]
): ProcessSummaryLine[] {
  const active = processes.filter((p) => !['COMPLETED', 'CANCELLED', 'FAILED'].includes(p.status));
  const byType = new Map<OperationalProcessType, OperationalProcess>();
  for (const p of active) {
    const prev = byType.get(p.type);
    if (!prev || new Date(p.createdAt) > new Date(prev.createdAt)) {
      byType.set(p.type, p);
    }
  }
  return Array.from(byType.values())
    .sort((a, b) => a.type.localeCompare(b.type))
    .map((p) => {
      const overdue = p.ownership?.overdue.isOverdue ?? p.sla.overdue;
      return {
        id: `proc:${p.id}`,
        label: PROCESS_TYPE_LABELS[p.type],
        statusLabel: PROCESS_STATUS_LABELS[p.status] ?? p.status,
        tone: statusTone(p.status),
        overdue,
        responsibleDisplayName: p.ownership?.responsibleDisplayName ?? null,
        slaExpectedAt: formatSlaRemaining(p.sla.expectedResolutionAt),
        overdueDurationLabel: overdue ? p.ownership?.overdue.durationLabel ?? null : null,
        escalationCandidateDisplayName: overdue
          ? p.ownership?.overdue.escalationCandidateDisplayName ?? null
          : null
      };
    });
}
